import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { slug } from "../../boundaries/label.formatter";
import { lastPathSegment } from "../../boundaries/url-path.parser";
import { JOURNEY_ROLE } from "../../enums/scan";
import {
  pageDisplayLabel,
  rawPagePath,
} from "../pages/boundaries/page-label.parser";
import { clusterLabel } from "./estateFormat";
import type { CandidateJourney, JourneyStep } from "./journeyTypes";
import { type ClusterContext, clusterKey, routeClusterKey } from "./scanModel";

export function flowGrouping(path: CandidateJourney): {
  key: string;
  label: string;
} {
  const nonEntry = path.steps.filter(
    (step) => step.role !== JOURNEY_ROLE.ENTRY,
  );
  const branch = nonEntry.find(
    (step) => routeClusterKey(step.page.path) !== "home",
  );
  const terminal = path.steps.at(-1);
  const cluster =
    branch && routeClusterKey(branch.page.path) !== "home"
      ? routeClusterKey(branch.page.path)
      : terminal && routeClusterKey(terminal.page.path) !== "home"
        ? routeClusterKey(terminal.page.path)
        : null;
  if (cluster) {
    return {
      key: `cluster-${cluster}`,
      label: `${clusterLabel(cluster)} path`,
    };
  }
  const fallback = nonEntry[0] ?? terminal ?? path.steps[0];
  const fallbackPath = stablePathPart(fallback?.page.path ?? "structure");
  const role = fallback?.role ?? JOURNEY_ROLE.UNCLASSIFIED;
  return {
    key: `fallback-${role}-${fallbackPath}`,
    label: `${clusterLabel(fallbackPath)} path`,
  };
}

export function contentFlowLabel(
  paths: CandidateJourney[],
  pages: OverviewResponse["pages"],
  context: ClusterContext,
): string {
  return `${contentFlowSubject(paths, pages, context)} path`;
}

/** Preserves the pre-Current Paths label ordering used as a final rank tie-break. */
export function flowCompatibilitySortKey(
  paths: CandidateJourney[],
  pages: OverviewResponse["pages"],
  context: ClusterContext,
): string {
  return `${contentFlowSubject(paths, pages, context)} flow`;
}

function contentFlowSubject(
  paths: CandidateJourney[],
  pages: OverviewResponse["pages"],
  context: ClusterContext,
): string {
  const represented = new Map(
    paths.flatMap((path) =>
      path.steps.flatMap((step) =>
        step.role === JOURNEY_ROLE.ENTRY
          ? []
          : [[step.page.id, step.page] as const],
      ),
    ),
  );
  const counts = new Map<string, number>();
  for (const page of represented.values()) {
    const key = clusterKey(page.path, context);
    if (key === "home") continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const ranked = [...counts.entries()].sort(
    ([leftKey, leftCount], [rightKey, rightCount]) =>
      rightCount - leftCount || leftKey.localeCompare(rightKey),
  );
  const total = ranked.reduce((sum, [, count]) => sum + count, 0);
  const dominant = ranked[0];
  if (!dominant) return "Structural";
  if (ranked.length > 1 && dominant[1] / total < 0.6) {
    return "Cross-area";
  }
  const evidence = pages.filter(
    (page) => clusterKey(page.path, context) === dominant[0],
  );
  return clusterLabel(dominant[0], evidence);
}

export function compactRouteLabel(
  path: CandidateJourney,
  site: OverviewResponse["site"],
  context?: ClusterContext,
): string {
  return routeLabelAtDetail(path, site, 1, context);
}

export function uniqueRouteLabels(
  paths: CandidateJourney[],
  site: OverviewResponse["site"],
  context?: ClusterContext,
): Map<string, string> {
  const unique = [
    ...new Map(paths.map((path) => [path.id, path] as const)).values(),
  ];
  const labels = new Map(
    unique.map((path) => [path.id, compactRouteLabel(path, site, context)]),
  );
  const maxDetail = Math.max(
    1,
    ...unique.map((path) => meaningfulMiddleSteps(path.steps, context).length),
  );
  for (let detail = 2; detail <= maxDetail; detail += 1) {
    for (const group of duplicateLabelGroups(unique, labels)) {
      for (const path of group) {
        labels.set(path.id, routeLabelAtDetail(path, site, detail, context));
      }
    }
  }
  for (const group of duplicateLabelGroups(unique, labels)) {
    for (const path of group) {
      labels.set(
        path.id,
        `${labels.get(path.id)} / ${routeDifferentiator(path, group)}`,
      );
    }
  }
  for (const group of duplicateLabelGroups(unique, labels)) {
    group
      .sort(
        (left, right) =>
          left.rank - right.rank || left.id.localeCompare(right.id),
      )
      .forEach((path, index) => {
        labels.set(path.id, `${labels.get(path.id)} / Route ${index + 1}`);
      });
  }
  return labels;
}

function routeLabelAtDetail(
  path: CandidateJourney,
  site: OverviewResponse["site"],
  detail: number,
  context?: ClusterContext,
): string {
  const first = path.steps[0];
  const last = path.steps.at(-1);
  if (!first || !last) return "Structural route";
  const meaningful = meaningfulMiddleSteps(path.steps, context);
  const includedMiddle =
    detail === 1
      ? meaningful.slice(-1)
      : [...meaningful.slice(0, detail - 1), ...meaningful.slice(-1)];
  const includedIds = new Set(includedMiddle.map((step) => step.page.id));
  const steps = [
    first,
    ...path.steps.slice(1, -1).filter((step) => includedIds.has(step.page.id)),
    last,
  ].filter(
    (step, index, all) =>
      all.findIndex((candidate) => candidate.page.id === step.page.id) ===
      index,
  );
  return steps
    .map((step) => compact(pageDisplayLabel(step.page, site), 30))
    .join(" -> ");
}

function meaningfulMiddleSteps(
  steps: JourneyStep[],
  context?: ClusterContext,
): JourneyStep[] {
  const middle = steps.slice(1, -1);
  const meaningful = middle.filter(
    (step) =>
      step.role !== JOURNEY_ROLE.ENTRY &&
      clusterKey(step.page.path, context) !== "home",
  );
  return meaningful.length > 0 ? meaningful : middle;
}

function duplicateLabelGroups(
  paths: CandidateJourney[],
  labels: Map<string, string>,
): CandidateJourney[][] {
  const groups = new Map<string, CandidateJourney[]>();
  for (const path of paths) {
    const label = labels.get(path.id) ?? "Structural route";
    groups.set(label, [...(groups.get(label) ?? []), path]);
  }
  return [...groups.values()].filter((group) => group.length > 1);
}

function routeDifferentiator(
  path: CandidateJourney,
  group: CandidateJourney[],
): string {
  for (let index = 0; index < path.steps.length; index += 1) {
    const values = new Set(
      group.map((candidate) => candidate.steps[index]?.page.path ?? ""),
    );
    if (values.size > 1) {
      const step = path.steps[index];
      if (step) return compact(rawPagePath(step.page), 28);
    }
  }
  return clusterLabel(stablePathPart(path.steps.at(-1)?.page.path ?? ""));
}

function stablePathPart(path: string): string {
  return slug(lastPathSegment(path) ?? "") || "structure";
}

function compact(value: string, limit: number): string {
  return value.length > limit
    ? `${value.slice(0, limit - 3).trimEnd()}...`
    : value;
}
